import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanCategorySection = ({ 
  title, 
  description, 
  plans, 
  onPlanSelect, 
  icon = 'BookOpen',
  enrolledPlans = [] 
}) => {
  if (!plans?.length) return null;

  return (
    <section className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center space-x-3">
        <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
          <Icon name={icon} size={18} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {title}
          </h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <PlanCard
            key={plan?.id}
            plan={plan}
            onSelect={onPlanSelect}
            isEnrolled={enrolledPlans?.some(ep => ep?.reading_plan_id === plan?.id)}
          />
        ))}
      </div>
    </section>
  );
};

const PlanCard = ({ plan, onSelect, isEnrolled = false }) => {
  // Calculate estimated reading time per day
  const estimatedTime = plan?.estimated_daily_reading_time || 15;
  const durationDays = plan?.duration_days || 365;
  const totalReadings = plan?.total_readings || durationDays;
  const tags = plan?.tags || [];
  const enrolledCount = plan?.enrolled_count || 0;
  const completedCount = plan?.completed_count || 0;

  return (
    <div className={`bg-card border border-border rounded-xl p-6 hover:shadow-warm transition-all duration-200 group ${
      isEnrolled ? 'opacity-50 grayscale' : ''
    }`}>
      {/* Plan Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
            {plan?.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {plan?.description}
          </p>
        </div>
      </div>

      {/* Plan Stats */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Duration:</span>
          <span className="font-medium text-foreground">{durationDays} days</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Daily time:</span>
          <span className="font-medium text-foreground">~{estimatedTime} min</span>
        </div>

        {totalReadings && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total readings:</span>
            <span className="font-medium text-foreground">{totalReadings}</span>
          </div>
        )}

        {/* Enrolled + Completed counts */}
        {(enrolledCount > 0 || completedCount > 0) && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Community:</span>
            <div className="flex items-center gap-2">
              {enrolledCount > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium text-foreground">
                  <Icon name="Users" size={12} color="var(--color-primary)" />
                  {enrolledCount} enrolled
                </span>
              )}
              {completedCount > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium text-success">
                  <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                  {completedCount} completed
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tags */}
      {tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {tags?.slice(0, 3)?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium"
            >
              {tag}
            </span>
          ))}
          {tags?.length > 3 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
              +{tags?.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Features */}
      {plan?.is_featured && (
        <div className="flex items-center space-x-2 mb-4 text-sm text-accent">
          <Icon name="Star" size={14} />
          <span className="font-medium">Featured Plan</span>
        </div>
      )}

      {/* Enrollment Status */}
      {isEnrolled && (
        <div className="flex items-center space-x-2 mb-4 text-sm text-success">
          <Icon name="CheckCircle" size={14} />
          <span className="font-medium">Already Enrolled</span>
        </div>
      )}

      {/* Enroll Button */}
      <Button
        onClick={() => onSelect?.(plan)}
        className="w-full"
        variant="outline"
        disabled={isEnrolled}
      >
        <Icon name={isEnrolled ? "CheckCircle" : "Plus"} size={16} />
        {isEnrolled ? 'Enrolled' : 'Enroll in Plan'}
      </Button>
    </div>
  );
};

export default PlanCategorySection;